// Pestaña «Método y fuentes»: qué se hizo, con qué datos, qué NO se puede afirmar
// y qué necesitamos del municipio para ir a producción. Honesto por diseño.

import { Bot, Database, FileCheck2, Satellite, ShieldAlert } from 'lucide-react';

import { Card } from '@/modules/orion-shell/ui';
import { CATEGORIAS, ORDEN_CATEGORIAS, fechaCorta, type Resumen } from './data';

const FUENTES = [
  { nombre: 'Sentinel-2 L2A (Copernicus / ESA)', uso: 'Pérdida de vegetación y nueva superficie construida o desnuda, a 10 m', lic: 'Licencia libre Copernicus', via: 'Element84 Earth Search (STAC abierto)' },
  { nombre: 'Meta / WRI Global Canopy Height 1 m', uso: 'Altura del dosel y % de arbolado por colonia', lic: 'CC-BY 4.0', via: 'AWS Open Data · Tolan et al., 2024' },
  { nombre: 'ESA WorldCover 2021', uso: 'Contexto: qué había antes (cultivo, matorral, árboles…)', lic: 'CC-BY 4.0', via: 'AWS Open Data' },
  { nombre: 'OpenStreetMap', uso: 'Colonias y límites municipales', lic: 'ODbL', via: 'Overpass / Nominatim' },
  { nombre: 'OpenAerialMap (dron)', uso: 'Demostración del conteo árbol por árbol (Naucalpan, Querétaro)', lic: 'CC-BY 4.0', via: 'Autores citados en cada imagen' },
];

const NECESITAMOS = [
  'Sus tomas satelitales o aéreas: resolución (ideal ≤ 0.3 m), fechas (una base y una reciente) y licencia de uso para derivados.',
  'Inventario de arbolado, aunque sea de 2–3 colonias, para validar y reportar precisión.',
  'Polígonos de licencias de construcción y fraccionamientos autorizados, con fecha, para cruzar cada hotspot con «¿tenía licencia?».',
  'Catálogo oficial de colonias, predios o catastro, áreas protegidas y árboles patrimoniales.',
  'La tabla de compensación arbórea vigente, para estimar la compensación por hotspot.',
  'Una GPU (p. ej. NVIDIA L4 o A10) para correr SAM 3 o YOLO/DeepForest sobre toda la ciudad.',
];

export function MetodoView({ resumen }: { resumen: Resumen }) {
  const v = resumen.vegetacion;
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Card title={<span className="flex items-center gap-2"><Satellite size={17} aria-hidden /> Cómo se detecta la pérdida de vegetación</span>}>
        <ol className="flex list-decimal flex-col gap-2 pl-5 text-sm leading-relaxed text-foreground">
          <li>Dos compuestos de temporada seca sin nubes: {v.fechas_antes.map(fechaCorta).join(', ')} contra {v.fechas_despues.map(fechaCorta).join(', ')} (mediana de 4 fechas cada uno).</li>
          <li>Índice de vegetación (NDVI) e índice de superficie construida o desnuda (NDBI) en cada píxel de 10 m.</li>
          <li>Pérdida = NDVI antes ≥ {v.umbrales.ndvi_antes_min}, caída ≥ {Math.abs(v.umbrales.dndvi_max)} y NDVI después &lt; {v.umbrales.ndvi_despues_max}. Se agrupan los píxeles y se quedan los polígonos de {v.umbrales.area_min_ha} ha o más.</li>
          <li>Cada polígono se explica con su cambio de NDBI y con lo que había en 2021 (ESA WorldCover), y se asigna a su municipio.</li>
        </ol>
        <div className="mt-4 flex flex-col gap-2">
          {ORDEN_CATEGORIAS.map((c) => (
            <div key={c} className="flex items-start gap-2 text-sm">
              <span className={`mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${CATEGORIAS[c].chip}`}>{CATEGORIAS[c].corto}</span>
              <span className="text-muted-foreground">{CATEGORIAS[c].ayuda}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card title={<span className="flex items-center gap-2"><ShieldAlert size={17} aria-hidden /> Qué NO afirma este análisis</span>}>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-sm leading-relaxed text-foreground">
          <li><b>Sin validación de campo.</b> Son estimaciones del satélite y de modelos, no un inventario.</li>
          <li>A 10 m no se distingue el suelo desnudo de una construcción: «probable obra» quiere decir que se quitó vegetación y quedó superficie dura o desnuda.</li>
          <li>Las parcelas de riego producen falsos positivos; WorldCover los reduce, pero no los elimina.</li>
          <li>No detecta cambios menores a ~1 ha ni árboles aislados.</li>
          <li>El mapa de dosel usa imagen de 2020: no refleja talas posteriores.</li>
          <li>El conteo árbol por árbol se muestra en otras ciudades porque no hay imagen abierta de alta resolución de la capital.</li>
        </ul>
      </Card>

      <Card title={<span className="flex items-center gap-2"><Database size={17} aria-hidden /> Fuentes y licencias</span>}>
        <ul className="flex flex-col divide-y divide-border">
          {FUENTES.map((f) => (
            <li key={f.nombre} className="py-2.5 first:pt-0 last:pb-0">
              <p className="text-sm font-medium text-foreground">{f.nombre}</p>
              <p className="text-xs text-muted-foreground">{f.uso}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">{f.lic} · {f.via}</p>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-[11px] text-muted-foreground">
          No se usan mapas base de Google ni Esri como insumo de análisis (sus términos lo prohíben). El mapa base de CARTO (cuando la instalación tiene llave) es solo fondo visual; sin llave, el mapa muestra únicamente las capas de datos y los compuestos Sentinel-2.
        </p>
        <p className="mt-2 text-[11px] text-muted-foreground">
          <b>ODbL:</b> el % de dosel por colonia (<code>dosel_colonias.geojson</code>) usa polígonos de OpenStreetMap; es una base de datos derivada de OpenStreetMap y se ofrece bajo la licencia ODbL 1.0 (© colaboradores de OpenStreetMap).
        </p>
      </Card>

      <Card title={<span className="flex items-center gap-2"><FileCheck2 size={17} aria-hidden /> Qué necesitamos del municipio</span>}>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-sm leading-relaxed text-foreground">
          {NECESITAMOS.map((n) => <li key={n}>{n}</li>)}
        </ul>
      </Card>

      <Card className="lg:col-span-2" title={<span className="flex items-center gap-2"><Bot size={17} aria-hidden /> Cómo lo usará el agente</span>}>
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { t: 'territorio.perdida_vegetacion', a: '(zona, desde, hasta)', d: 'Hotspots con área, municipio, categoría y explicación. «¿Cuánta vegetación perdió Soledad desde 2019?»' },
            { t: 'territorio.dosel_colonia', a: '(colonia)', d: '% de dosel, árboles estimados y fecha de la imagen. «¿Qué colonias necesitan reforestación?»' },
            { t: 'territorio.conteo_arboles', a: '(polígono)', d: 'Se activa con imagen ≤ 0.3 m del municipio; mientras no la haya, el agente dice que no hay imagen suficiente.' },
          ].map((x) => (
            <div key={x.t} className="rounded-xl border border-border p-3">
              <p className="font-mono text-[12.5px] text-foreground">{x.t}<span className="text-muted-foreground">{x.a}</span></p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{x.d}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground">Herramientas de solo lectura, limitadas a la org. Estado: diseño; los datos de esta app ya son los que consultarían.</p>
      </Card>
    </div>
  );
}
